import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateTrigGraphVariations,
  trigGraphElements,
  normalizeStep1Types,
  trigEquationToLatex,
  formatPiValue,
  computeAsymptotesInOnePeriod,
  STEP1_TYPE_LABELS,
} from './trigGraphUtils';

/**
 * Read-only view for reviewing/grading trig graph answers
 *
 * Props:
 * - config: TrigGraphConfig
 * - studentAnswer: StudentAnswer
 * - correctAnswer: { correctGraphIndex: number }
 * - isCorrect: boolean
 */
const TrigGraphReadOnly = ({
  config,
  studentAnswer,
  correctAnswer,
  isCorrect,
}) => {
  const { parsedTrig, grid } = config || {};

  const step1Types = normalizeStep1Types(config || {});
  const step1TypesKey = step1Types.join(',');

  // Generate variations
  const variations = useMemo(() => {
    if (!parsedTrig) return [];
    try {
      const { variations } = generateTrigGraphVariations(
        parsedTrig, step1Types, config.variationSeed
      );
      return variations;
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return [];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedTrig, step1TypesKey, config?.variationSeed]);

  const studentGraphIndex = studentAnswer?.step2?.selectedGraphIndex;
  const correctGraphIndex = correctAnswer?.correctGraphIndex ?? config?.correctGraphIndex;

  if (!config || !parsedTrig) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  // Map of typed-input label fields stored alongside numerics
  const labelFieldFor = {
    amplitude: 'amplitudeLabel',
    verticalShift: 'verticalShiftLabel',
    yIntercept: 'yInterceptLabel',
  };

  // Build step1 display data
  const step1Display = [];
  for (const t of step1Types) {
    const studentVal = studentAnswer?.step1?.[t];
    const correctVal = parsedTrig[t];
    const label = STEP1_TYPE_LABELS[t] || t;

    if (t === 'verticalAsymptotes') {
      const studentVAs = studentAnswer?.step1?.verticalAsymptotes || [];
      if (studentVAs.length > 0) {
        step1Display.push({ label, values: studentVAs.map(v => `x = ${v}`) });
      } else {
        // Fall back to correct asymptote values
        const correctVAs = computeAsymptotesInOnePeriod(parsedTrig);
        step1Display.push({
          label,
          values: correctVAs.length > 0
            ? correctVAs.map(v => `x = ${formatPiValue(v)}`)
            : ['—'],
        });
      }
    } else {
      // Prefer the student's typed label when present (preserves fraction vs decimal form)
      const studentLabel = labelFieldFor[t] ? studentAnswer?.step1?.[labelFieldFor[t]] : null;
      const displayVal = studentLabel != null
        ? studentLabel
        : (studentVal != null ? studentVal : (correctVal != null ? formatPiValue(correctVal) : '—'));
      step1Display.push({
        label,
        values: [`${displayVal}`],
      });
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Overall Result */}
      {typeof isCorrect === 'boolean' && (
        <Alert severity={isCorrect ? 'success' : 'error'}>
          {isCorrect ? 'Correct!' : 'Incorrect'}
        </Alert>
      )}

      {/* Equation Display */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(trigEquationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Step 1 Answers */}
      <Paper sx={{ p: 2 }}>
        {step1Display.map((item, idx) => (
          <Box key={item.label} sx={{ mb: idx < step1Display.length - 1 ? 2 : 0 }}>
            <Typography variant="h6" gutterBottom>
              {item.label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {item.values.map((v, i) => (
                <Chip
                  key={i}
                  label={v}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        ))}
      </Paper>

      {/* Graph Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Graph Selection
        </Typography>

        <Grid container spacing={2}>
          {variations.map((variation, index) => {
            const isStudentChoice = index === studentGraphIndex;
            const isCorrectChoice = index === correctGraphIndex;

            return (
              <Grid item xs={6} key={variation.label}>
                <Paper
                  sx={{
                    p: 1,
                    border: isCorrectChoice
                      ? '3px solid green'
                      : isStudentChoice
                      ? '3px solid red'
                      : '1px solid grey',
                    bgcolor: isCorrectChoice
                      ? 'success.light'
                      : isStudentChoice && !isCorrectChoice
                      ? 'error.light'
                      : 'background.paper',
                    position: 'relative',
                  }}
                >
                  {/* Status indicators */}
                  <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                    {isStudentChoice && (
                      <Chip
                        size="small"
                        label="Your answer"
                        color={isCorrectChoice ? 'success' : 'error'}
                        icon={isCorrectChoice ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    )}
                    {isCorrectChoice && !isStudentChoice && (
                      <Chip
                        size="small"
                        label="Correct"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>

                  <Typography variant="subtitle2" align="center" sx={{ mt: 3 }}>
                    Graph {variation.label}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CoordinateGrid
                      config={grid}
                      elements={trigGraphElements(variation.trig, grid)}
                      pendingPoints={[]}
                      mode="point"
                      readOnly={true}
                      width={180}
                      height={180}
                    />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default TrigGraphReadOnly;
